
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { markPlanExpired } from './SubscriptionRedirectHelper';
import { getStoredReferralCode, attributeReferralToSubscription } from './AffiliateUtils';

export async function assignFreePlan(userId: string): Promise<boolean> {
  try {
    // Check if there's a stored referral code for attribution
    const referralCode = getStoredReferralCode();
    if (referralCode) {
      console.log('Found stored referral code for assignment:', referralCode);
    }

    // Get the Free/Starter plan ID from the subscription_plans table
    // Look for both "Free" and "Starter" to handle both possibilities
    const { data: freePlan, error: planError } = await supabase
      .from('subscription_plans')
      .select('id')
      .or('name.eq.Free,name.eq.Starter')
      .single();
    
    if (planError || !freePlan) {
      console.error('Error fetching Free/Starter plan:', planError);
      return false;
    }
    
    // Check if user already has an active subscription
    const { data: existingSubscription } = await supabase
      .from('user_subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active')
      .single();
      
    if (existingSubscription) {
      console.log('User already has an active subscription');
      return true; // User already has a subscription
    }
    
    // Calculate the end date (7 days from now)
    const currentDate = new Date();
    const currentPeriodEnd = new Date();
    currentPeriodEnd.setDate(currentDate.getDate() + 7);
    
    // Create a new subscription record
    const { data: subscriptionData, error: subscriptionError } = await supabase
      .from('user_subscriptions')
      .insert({
        user_id: userId,
        plan_id: freePlan.id,
        status: 'active',
        current_period_start: currentDate.toISOString(),
        current_period_end: currentPeriodEnd.toISOString(),
        is_trial: false,
        billing_cycle: 'one-time'
      })
      .select()
      .single();
    
    if (subscriptionError) {
      console.error('Error creating Free plan subscription:', subscriptionError);
      return false;
    }
    
    // Attribute referral if found
    if (referralCode && subscriptionData) {
      try {
        const attributed = await attributeReferralToSubscription(subscriptionData.id, userId);
        if (attributed) {
          console.log('Successfully attributed referral to free plan subscription');
        } else {
          console.warn('Failed to attribute referral to subscription');
        }
      } catch (err) {
        console.error('Error attributing referral to subscription:', err);
        // Continue execution as this is not critical
      }
    }
    
    console.log('Free plan assigned successfully to user:', userId);
    return true;
  } catch (err) {
    console.error('Error in assignFreePlan:', err);
    return false;
  }
}

export async function checkFreePlanExpiry(): Promise<{
  expired: boolean;
  daysRemaining?: number;
  message?: string;
}> {
  try {
    // Use the new unified check-subscription-expiry edge function
    const { data, error } = await supabase.functions.invoke('check-subscription-expiry');
    
    if (error) {
      console.error('Error checking subscription expiry:', error);
      return { expired: false };
    }
    
    // If the plan is about to expire, activate grace period
    if (data && data.plan_type === 'free' && data.days_remaining <= 1) {
      markPlanExpired('free');
    }
    
    return {
      expired: data?.expired || false,
      daysRemaining: data?.days_remaining,
      message: data?.message || (data?.expired ? 'Free plan expired' : `Free plan active with ${data?.days_remaining} days remaining`)
    };
  } catch (err) {
    console.error('Error in checkFreePlanExpiry:', err);
    return { expired: false };
  }
}

export function showFreePlanNotification(daysRemaining: number): void {
  if (daysRemaining <= 0) {
    toast.warning(
      `Your Free plan has expired! Upgrade now to maintain access to all features.`,
      {
        id: 'free-plan-expired',
        duration: 10000,
        action: {
          label: 'Upgrade Now',
          onClick: () => window.location.href = '/subscription/plans'
        }
      }
    );
  } else if (daysRemaining <= 1) {
    toast.warning(
      `Your Free plan expires today! Upgrade now to maintain access to features.`,
      {
        id: 'free-plan-expiring-today',
        duration: 10000,
        action: {
          label: 'Upgrade',
          onClick: () => window.location.href = '/subscription/plans'
        }
      }
    );
  } else if (daysRemaining <= 3) {
    toast.info(
      `Your Free plan expires in ${daysRemaining} days. Consider upgrading to maintain access.`,
      {
        id: 'free-plan-expiring-soon',
        duration: 8000,
        action: {
          label: 'View Plans',
          onClick: () => window.location.href = '/subscription/plans'
        }
      }
    );
  }
}
